import { useLayoutEffect } from "react";

import { useSearchParams } from "react-router-dom";

import Typography from "@mui/material/Typography";

import useIntegration from "@app/analytics/hooks/integrations/useIntegration";
import useSlackIntegration from "@app/analytics/hooks/integrations/useSlackIntegration";
import IntegrationDetailHeader from "@app/analytics/Integrations/components/IntegrationDetailHeader";
import IntegrationError from "@app/analytics/Integrations/components/IntegrationError";
import IntegrationNotConnected from "@app/analytics/Integrations/components/IntegrationNotConnected";
import IntegrationUpsell from "@app/analytics/Integrations/components/IntegrationUpsell";
import { PROVIDER_CONTENT } from "@app/analytics/Integrations/constants";
import {
  IntegrationDetailContent,
  LoadingIntegrationDetail,
} from "@app/analytics/Integrations/styles";
import useAuth from "@app/common/auth/useAuth";
import { MESSAGING_BOT_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import { toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import {
  isWorkspaceOwner,
  isWorkspaceAdmin,
} from "@app/common/workspaces/workspaceUtils";

import { Link } from "@rui/Link";

const WorkspaceSlackIntegrationConfig = () => {
  const [queryParams] = useSearchParams();

  const { user } = useAuth();
  const { inWorkspace, workspaceId } = useWorkspace();

  const { authorize } = useIntegration();
  const {
    disconnect,
    workspaceIntegration: integration,
    isLoading,
  } = useSlackIntegration();

  const reason = queryParams.get("reason");
  const action = queryParams.get("action");
  const isSuccess = queryParams.get("success") === "true";

  useLayoutEffect(() => {
    if (action !== MESSAGING_BOT_PROVIDERS_BY_ID.slack_bot.connectAction) {
      return;
    }

    if (isSuccess) {
      toast.success(`${integration.displayName} connected successfully!`);
    } else if (reason === "workspace_not_found") {
      toast.info(
        `You must be part of a Workspace to add the ${integration.displayName} integration.`
      );
    } else {
      toast.error(
        `There was an error connecting ${integration.displayName}. Please try again or reach out to support@read.ai if the problem persists.`
      );
    }
  }, [reason, action, isSuccess]);

  if (!inWorkspace) {
    return <IntegrationUpsell type="workspace" />;
  }

  if (isLoading) {
    return <LoadingIntegrationDetail />;
  }

  if (!integration) {
    return <IntegrationError />;
  }

  const handleClickConnect = () => {
    authorize.mutate({
      providerId: integration.providerId,
      returnRoute: integration.route,
      scope: "workspace",
    });
  };

  const handleDisconnectSlack = () => {
    disconnect.mutate("workspace", {
      onError: () => {
        toast.error(
          `There was an error disconnecting ${integration.displayName}. Please try again or log into your ${integration.displayName} account to disconnect Read.`
        );
      },
    });
  };

  const userIsWorkspaceOwner = isWorkspaceOwner(user?.workspace);

  const connectCta = {
    disabled: !userIsWorkspaceOwner,
    isLoading: authorize.isPending,
    onClick: handleClickConnect,
    tooltip: "Contact your Workspace owner to integrate Slack with Read",
  };

  const providerContent = PROVIDER_CONTENT[integration.providerId];

  return (
    <>
      <IntegrationDetailHeader
        cta={connectCta}
        provider={integration}
        documentationUrl={integration.documentationUrl}
        isDisconnecting={disconnect.isPending}
        onClickDisconnect={handleDisconnectSlack}
        onReauth={handleClickConnect}
      >
        <Typography variant="bodySecondaryReg" color="shades.neutral.600">
          {providerContent.workspaceShortDescription}
        </Typography>
      </IntegrationDetailHeader>
      {integration.isConnected ? (
        <IntegrationDetailContent spacing={1.5}>
          <Typography variant="bodySecondaryMed" color="shades.neutral.800">
            Configuration
          </Typography>
          <Typography variant="bodySecondaryReg" color="shades.neutral.600">
            {userIsWorkspaceOwner ? "You have" : "Your Workspace owner has"}{" "}
            successfully connected Read to {integration.displayNameShort}. The
            Read App can automatically message meeting recaps to meeting
            participants.
            {isWorkspaceAdmin(user?.workspace) && (
              <ul>
                <li>
                  Visit{" "}
                  <Link
                    fontWeight={400}
                    to={ROUTES.workspaceSettingsSettingsById(workspaceId!)}
                    underline
                  >
                    Workspace Settings{" > "}Reporting{" > "}Distribution
                  </Link>{" "}
                  to manage meeting recap distribution for your Workspace
                </li>
              </ul>
            )}
          </Typography>
        </IntegrationDetailContent>
      ) : (
        <IntegrationNotConnected cta={connectCta} provider={integration} />
      )}
    </>
  );
};

export default WorkspaceSlackIntegrationConfig;
